<script setup lang="ts">
// The Owner's Manual — accordion guide library. Each guide answers the
// question a salesperson usually answers with an upsell.
import { NCollapse, NCollapseItem } from 'naive-ui'
import { GUIDES } from '../content/guides'
</script>

<template>
    <section id="manual" class="site-section manual">
        <span class="watermark" aria-hidden="true">?</span>
        <div class="site-content narrow">
            <span class="kicker" v-reveal>The library</span>
            <h2 v-reveal>The Owner's Manual</h2>
            <p class="muted intro">
                Everything a homeowner actually needs to know about the equipment — UV lights,
                add-ons, tiers, refrigerants, ratings, sizing, humidity — minus the sales pitch.
            </p>

            <NCollapse accordion>
                <NCollapseItem v-for="guide in GUIDES" :key="guide.id" :name="guide.id">
                    <template #header>
                        <div class="head">
                            <span class="kicker">{{ guide.kicker }}</span>
                            <strong>{{ guide.title }}</strong>
                            <span class="muted teaser">{{ guide.teaser }}</span>
                        </div>
                    </template>
                    <article class="guide">
                        <template v-for="(section, i) in guide.sections" :key="i">
                            <h4 v-if="section.h">{{ section.h }}</h4>
                            <p v-if="section.p">{{ section.p }}</p>
                            <ul v-if="section.list">
                                <li v-for="item in section.list" :key="item">{{ item }}</li>
                            </ul>
                        </template>
                        <aside v-if="guide.stopDiy" class="stop-diy">
                            <strong>When to stop DIY-ing:</strong> {{ guide.stopDiy }}
                        </aside>
                    </article>
                </NCollapseItem>
            </NCollapse>
        </div>
    </section>
</template>

<style scoped lang="scss">
.manual {
    position: relative;
    overflow: hidden;
}

.watermark {
    position: absolute;
    top: -0.18em;
    right: -0.05em;
    font-family: var(--site-font-heading);
    font-style: italic;
    font-size: clamp(16rem, 30vw, 30rem);
    line-height: 1;
    color: var(--site-accent-soft);
    user-select: none;
    pointer-events: none;
}

.narrow {
    position: relative;
    max-width: 52rem;
}

.intro {
    max-width: 40rem;
    margin-bottom: var(--site-space-lg);
}

.head {
    display: flex;
    flex-direction: column;
    gap: 2px;
    padding: var(--site-space-sm) 0;

    .kicker {
        font-size: 0.75rem;
        font-weight: 700;
        letter-spacing: 0.08em;
        text-transform: uppercase;
        color: var(--site-accent);
    }

    strong {
        font-family: var(--site-font-heading);
        font-size: 1.1rem;
        color: var(--site-text);
    }

    .teaser {
        font-size: 0.9rem;
    }
}

.guide {
    max-width: 65ch;
    padding-bottom: var(--site-space-md);

    h4 {
        margin: var(--site-space-lg) 0 var(--site-space-xs);
        font-size: 1rem;
    }

    ul {
        margin: 0 0 var(--site-space-md);
        padding-left: 1.3rem;
        display: grid;
        gap: var(--site-space-xs);
    }

    .stop-diy {
        margin-top: var(--site-space-lg);
        padding: var(--site-space-md);
        border-left: 3px solid var(--site-warning);
        background: var(--site-bg-alt);
        border-radius: 0 var(--site-radius-sm) var(--site-radius-sm) 0;
        font-size: 0.95rem;
    }
}
</style>
